import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AiUsageService } from "@/services/analytics/AiUsageService";
import { AiUsagePeriod, AiUsageTabId } from "@/types/admin/ai-usage";
import { AI_USAGE_TABS, DEFAULT_TAB, PERIOD_OPTIONS } from "./constants";
import ModelsTab from "./ModelsTab";
import UsageTab from "./UsageTab";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const isTabId = (value: string | null): value is AiUsageTabId => AI_USAGE_TABS.some((t) => t.id === value);

const AiUsagePage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<AiUsagePeriod>("30d");
  const rawTab = searchParams.get("tab");
  const activeTabId: AiUsageTabId = isTabId(rawTab) ? rawTab : DEFAULT_TAB;

  const { data: report, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["ai-usage-report", period],
    queryFn: () => AiUsageService.report(period),
  });

  const syncMutation = useMutation({
    mutationFn: AiUsageService.syncPrices,
    onSuccess: ({ models, backfilled }) => {
      toast.success(
        `Prices refreshed for ${models} models${backfilled ? `, ${backfilled} earlier calls priced` : ""}`,
      );
      queryClient.invalidateQueries({ queryKey: ["ai-usage-report"] });
    },
    onError: (err: Error) => toast.error(err.message ?? "Could not refresh prices"),
  });

  const handleSelectTab = (id: AiUsageTabId) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_TAB) next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
      <div className="container mx-auto max-w-[1920px] w-full px-4 py-5 md:py-6">
        {/* WorkspaceSwitcher owns the top-right corner from md up, so the period picker stops short of it. */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between md:pr-[104px]">
          <div className="flex flex-wrap items-center gap-2">
            {AI_USAGE_TABS.map((tab) =>
              tab.id === activeTabId ? (
                <button
                  key={tab.id}
                  onClick={() => handleSelectTab(tab.id)}
                  className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md shadow-genie-primary/20"
                >
                  <span className="flex min-h-[36px] items-center gap-2 px-3.5 py-2 rounded-[10px] bg-white text-sm font-semibold text-[#222B45]">
                    <GenieIcon name={tab.genieIcon} size={16} className="h-4 w-4" />
                    {tab.label}
                  </span>
                </button>
              ) : (
                <button
                  key={tab.id}
                  onClick={() => handleSelectTab(tab.id)}
                  className="flex min-h-[40px] items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border bg-white border-[#E5EAF0] text-gray-700 hover:border-genie-primary/40 hover:text-genie-primary transition-all"
                >
                  <GenieIcon name={tab.genieIcon} size={16} className="h-4 w-4" />
                  {tab.label}
                </button>
              ),
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={(v) => setPeriod(v as AiUsagePeriod)}>
              <SelectTrigger className="h-10 min-w-0 flex-1 rounded-xl border-[#E5EAF0] bg-white text-base font-medium text-gray-700 sm:w-44 sm:flex-none sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh AI usage"
              title="Refresh"
              className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-xl border border-[#E5EAF0] bg-white text-gray-700 transition-all hover:border-genie-primary/40 hover:text-genie-primary disabled:opacity-60 sm:min-h-[40px] sm:min-w-[40px]"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-[#E5EAF0] bg-white">
            <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
          </div>
        ) : error || !report ? (
          <div className="rounded-2xl border border-[#E5EAF0] bg-white px-4 py-12 text-center text-sm text-[#64748B]">
            {(error as Error)?.message ?? "Could not load AI usage."}
          </div>
        ) : activeTabId === "usage" ? (
          <UsageTab report={report} />
        ) : (
          <ModelsTab report={report} isSyncing={syncMutation.isPending} onSyncPrices={() => syncMutation.mutate()} />
        )}
      </div>
    </div>
  );
};

export default AiUsagePage;
